/**
 * @file Shared React component — components/common/ChangeEmailModal.tsx.
 */

import React, { useState } from "react";
import axiosClient from "../../api/axiosClient";
import "./ChangeEmailModal.css";
import Button from "./Button";
import OutlineButton from "./OutlineButton";

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
  currentEmail?: string;
  onSuccess?: () => void;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  open,
  onClose,
  currentEmail,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    password: "",
    newEmail: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(""); // Clear error on input change
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    if (!formData.password) {
      setError("Password is required");
      return false;
    }
    if (!formData.newEmail) {
      setError("New email is required");
      return false;
    }
    if (!validateEmail(formData.newEmail)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (currentEmail && formData.newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError("New email must be different from current email");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axiosClient.post("/profile/change-email", {
        password: formData.password,
        newEmail: formData.newEmail.trim().toLowerCase(),
      });

      if (response.data?.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          password: "",
          newEmail: "",
        });
        
        // Close modal after 1.5 seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        setError(response.data?.message || "Failed to change email");
      }
    } catch (err: any) {
      console.error("Change email error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to change email. Please check your password and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        password: "",
        newEmail: "",
      });
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div className="modal-card change-email-modal fade-in" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header">
          <h2>Change Email</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={loading}
            type="button"
          >
            ×
          </button>
        </div>

        {/* CONTENT */}
        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <p>Email changed successfully!</p>
              <p className="success-note">Please check your new email for verification.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="change-email-form">
              {currentEmail && (
                <div className="current-email-info">
                  <label>Current Email</label>
                  <p className="current-email-value">{currentEmail}</p>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Enter your password to confirm"
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newEmail">New Email Address *</label>
                <input
                  type="email"
                  id="newEmail"
                  name="newEmail"
                  value={formData.newEmail}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Enter your new email address"
                  autoComplete="email"
                />
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <OutlineButton
                  label="Cancel"
                  onClick={handleClose}
                  disabled={loading}
                  type="button"
                />
                <Button
                  label={loading ? "Changing..." : "Change Email"}
                  disabled={loading}
                  type="submit"
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/** React component — Change Email Modal. */
export default ChangeEmailModal;

